import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useAuthData, useUserRoles } from '@anytag/auth';
import { Form } from '@anytag/shared-atoms';
import { AutoManagedCampaignType } from '@anytag/graphql';
import {
  MIN_PRICE,
  MAX_PRICE,
  MIN_COMMISSION_RATE,
  MAX_COMMISSION_RATE,
  MIN_MAX_PRICE_OPTIONS,
  AUTO_MANAGED_COUNTRY_OPTIONS,
  MIN_MAX_COMMISSION_RATE_OPTIONS,
  AUTO_MANAGED_CAMPAIGN_TYPE_OPTIONS
} from '@anytag/shared-constants';
import { useAllInfluencerCategoryOptions, useTranslateCountryOptions } from '@anytag/shared-hooks';
import {
  SelectorField,
  TextSearchField,
  RangeSliderInputField,
  CustomShopeeCampaignsTrigger
} from '@anytag/shared-molecules';
import {
  MarketplaceAutoManagedCampaignFilterFormValues,
  MarketplaceAutoManagedCampaignFilterKeys
} from './schemaTypes';

interface TemplateProps {
  onSubmit?: () => void;
}

export const Template = ({ onSubmit }: TemplateProps) => {
  const { t } = useTranslation();
  const {
    auth: { currencyId }
  } = useAuthData();
  const { isAdminStaff } = useUserRoles();
  const { allInfluencerCategoryOptions } = useAllInfluencerCategoryOptions();
  const allAvailableAutoManagedCountriesTranslated = useTranslateCountryOptions(AUTO_MANAGED_COUNTRY_OPTIONS);
  const { getValues } = useFormContext<MarketplaceAutoManagedCampaignFilterFormValues>();
  const marketplaceType = getValues('marketplaceType');

  return (
    <>
      <div css={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <p css={{ fontSize: '16px', fontWeight: 600 }}>{t('Select Offer')}</p>
          <p css={{ fontSize: '14px', marginTop: '8px' }}>
            {t('Select offer that you want to provide with special deal')}
          </p>
        </div>
        {marketplaceType === AutoManagedCampaignType.SHOPEE && isAdminStaff ? (
          <CustomShopeeCampaignsTrigger className="self-start" />
        ) : null}
      </div>

      <Form.Fields>
        <SelectorField<MarketplaceAutoManagedCampaignFilterKeys>
          onSubmit={onSubmit}
          className="max-w-40"
          name="marketplaceType"
          options={AUTO_MANAGED_CAMPAIGN_TYPE_OPTIONS}
          placeholder={t<string>('Selector.Marketplace')}
        />
        <TextSearchField<MarketplaceAutoManagedCampaignFilterKeys>
          name="keyword"
          onSubmit={onSubmit}
          placeholder={t<string>('Search.Search by keyword, brand')}
        />
        <SelectorField<MarketplaceAutoManagedCampaignFilterKeys>
          name="countryId"
          onSubmit={onSubmit}
          className="max-w-40"
          placeholder={t<string>('Selector.Country')}
          options={allAvailableAutoManagedCountriesTranslated}
        />
        <SelectorField<MarketplaceAutoManagedCampaignFilterKeys>
          name="categoryIds"
          onSubmit={onSubmit}
          className="max-w-40"
          options={allInfluencerCategoryOptions}
          placeholder={t<string>('Selector.Categories')}
          multiple
        />
      </Form.Fields>

      <Form.Fields className="justify-end">
        <div className="flex max-w-80 basis-1/2 items-center">
          <p className="mr-2 text-base">{t('Commission rate')}</p>
          <RangeSliderInputField<MarketplaceAutoManagedCampaignFilterKeys>
            onSubmit={onSubmit}
            options={MIN_MAX_COMMISSION_RATE_OPTIONS}
            minField={{ name: 'minCommissionRate', placeholder: `${MIN_COMMISSION_RATE}%` }}
            maxField={{ name: 'maxCommissionRate', placeholder: `${MAX_COMMISSION_RATE}%` }}
            hideSlider
          />
        </div>
        <div className="ml-3 flex max-w-90 basis-1/2 items-center">
          {/* TODO: currently we are showing User's currency here, but BE has no idea about it, probably we need to update API with it */}
          <p className="mr-2 text-base">
            {t('Price')}
            {currencyId ? ` (${t(currencyId)})` : ''}
          </p>
          <RangeSliderInputField<MarketplaceAutoManagedCampaignFilterKeys>
            onSubmit={onSubmit}
            options={MIN_MAX_PRICE_OPTIONS}
            minField={{ name: 'minPrice', placeholder: String(MIN_PRICE) }}
            maxField={{ name: 'maxPrice', placeholder: `${MAX_PRICE}+` }}
            hideSlider
          />
        </div>
      </Form.Fields>
    </>
  );
};
